import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, getAccessToken, subscribeAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";

export interface NotifItem {
  id: string; message: string; issueId: string | null; link: string | null; kind: string;
  displayIdFormatted: string | null; createdAt: string; readAt: string | null;
}

type Category = "issues" | "other";

interface NotificationsData {
  issues: NotifItem[];
  other: NotifItem[];
  markRead: (category: Category, id: string) => void;
  markUnread: (category: Category, id: string) => void;
  /** The most recent SSE arrival, for the toast. Null until one lands. */
  lastArrival: NotifItem | null;
  /** True until the first fetch settles, so lists can show a skeleton rather
   *  than flashing "You're all caught up" before any data has arrived. */
  loading: boolean;
  markAllRead: (category: Category) => void;
  dismiss: (category: Category, id: string) => void;
  clearRead: (category: Category) => void;
  /** Refetches both lists now, without waiting for the 60s poll. */
  refresh: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsData | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [issues, setIssues] = useState<NotifItem[]>([]);
  const [other, setOther] = useState<NotifItem[]>([]);
  const [lastArrival, setLastArrival] = useState<NotifItem | null>(null);
  const [loading, setLoading] = useState(true);
  const setterFor = { issues: setIssues, other: setOther };

  async function loadAll() {
    try {
      const [issuesRes, otherRes] = await Promise.all([
        api.get<NotifItem[]>("/notifications?unreadOnly=false&category=issues"),
        api.get<NotifItem[]>("/notifications?unreadOnly=false&category=other"),
      ]);
      setIssues(issuesRes.data.slice(0, 20));
      setOther(otherRes.data.slice(0, 20));
    } catch (err) { logError("NotificationsContext.loadAll", err); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    let source: EventSource | null = null;

    function connect(token: string) {
      source?.close();
      source = new EventSource(`/api/notifications/sse?token=${encodeURIComponent(token)}`);
      source.onmessage = (e) => {
        if (e.data === "ping") return;
        let payload: Partial<NotifItem> & { kind?: string };
        try { payload = JSON.parse(e.data); } catch { return; }
        if (!payload.id || payload.kind === "connected") return;
        const notif = payload as NotifItem;
        setterFor[notif.issueId ? "issues" : "other"](prev => {
          if (prev.some(n => n.id === notif.id)) return prev;   // replay, not new
          setLastArrival(notif);
          return [notif, ...prev].slice(0, 20);
        });
      };
    }

    const initialToken = getAccessToken();
    if (initialToken) { loadAll(); connect(initialToken); }
    else setLoading(false);

    // 60s poll: refreshes data as a safety net AND is what recovers the SSE connection —
    // if the access token has rotated (8h TTL), the poll's own 401 triggers axios's existing
    // refresh flow (api.ts), which calls setAccessToken() and fires the listener below,
    // reconnecting EventSource with the fresh token. Native EventSource auto-reconnects on
    // transient drops on its own; this covers the one case it can't (a stale query-string token).
    const pollTimer = setInterval(loadAll, 60_000);

    const unsubscribe = subscribeAccessToken((token) => {
      if (token) { loadAll(); connect(token); }
      else { source?.close(); setIssues([]); setOther([]); setLoading(true); }
    });

    return () => {
      clearInterval(pollTimer);
      source?.close();
      unsubscribe();
    };
  }, []);

  function markRead(category: Category, id: string) {
    api.post(`/notifications/${id}/read`).catch(() => {});
    setterFor[category](prev => prev.map(n => n.id === id ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n));
  }

  function markUnread(category: Category, id: string) {
    api.post(`/notifications/${id}/unread`).catch(() => {});
    setterFor[category](prev => prev.map(n => n.id === id ? { ...n, readAt: null } : n));
  }

  function markAllRead(category: Category) {
    api.post("/notifications/read-all").catch(() => {});
    setterFor[category](prev => prev.map(n => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
  }

  function dismiss(category: Category, id: string) {
    api.delete(`/notifications/${id}`).catch(() => {});
    setterFor[category](prev => prev.filter(n => n.id !== id));
  }

  function clearRead(category: Category) {
    api.post("/notifications/clear-read").catch(() => {});
    setterFor[category](prev => prev.filter(n => !n.readAt));
  }

  return (
    <NotificationsContext.Provider value={{ issues, other, markRead, markUnread, markAllRead, dismiss, clearRead, lastArrival, loading, refresh: loadAll }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsData {
  const ctx = useContext(NotificationsContext);
  return ctx ?? { issues: [], other: [], markRead: () => {}, markUnread: () => {}, markAllRead: () => {}, dismiss: () => {}, clearRead: () => {}, lastArrival: null, loading: false, refresh: async () => {} };
}
